<template>
  <div v-if="show" class="scanner-wrapper">
    <div class="layout">
      <div class="container">
        <div id="canvas-container" class="canvas-container">
          <image-drawer
                  :image="image"
                  :points="points"
                  @input="onImageEdit"
          >
            <template #crop>
              <slot name="crop"></slot>
            </template>
            <template #erase>
              <slot name="erase"></slot>
            </template>
            <template #drag>
              <slot name="drag"></slot>
            </template>
            <template #zoomIn>
              <slot name="zoomIn"></slot>
            </template>
            <template #zoomOut>
              <slot name="zoomOut"></slot>
            </template>
            <template #otherBtn>
              <slot name="otherBtn"></slot>
            </template>
          </image-drawer>
          <receipt-scan-loader
                  v-if="isLoading"
                  :phrases="image ? phrases : cornerDetectionPhrases"
          ></receipt-scan-loader>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ImageDrawer from './ImageDrawer.vue'
import ReceiptScanLoader from './ReceiptScanLoader.vue'
import { useReceiptStore, useErrorStore } from '@/stores'

export default {
  name: 'receipt-scanner',
  components: {
    ReceiptScanLoader,
    ImageDrawer
  },
  props: {
    // main properties
    file: {
      type: [File,Blob],
      default: null
    },
    show:Boolean,
    // options
    phrases: {
      type: Array,
      required: true
    },
    cornerDetectionPhrases: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      isLoading: false,
      image: null,
      points: [],
      editedImage: null
    }
  },

  watch: {
    file (file) {
      if (!file) {
        if (this.image) {
          this.image = null
          this.points = null
          this.editedImage = null
        }
        if (this.isLoading) {
          this.isLoading = false
        }
      }
      this.onFileChanged(file)
    }
  },
  methods: {
    onFileChanged () {
      if (!this.file) return

      this.isLoading = true

      const formData = new FormData()
      formData.append('receipt', this.file)

      const receiptStore = useReceiptStore()
      const errorStore = useErrorStore()
      
      // First attempt: normal corner detection
      axios.post(`https://receipt.justsnap.eu/getConvertedImageWithPointsTF`, formData)
        .then(resp => {
          if (resp.data.response === 'OK') {
            // Check if points array is empty or null
            if (!resp.data.image || resp.data.image.length === 0) {
              // No receipt detected, fallback to binary mode
              console.log('No receipt detected, trying binary mode...')
              this.fallbackToBinaryMode(formData, receiptStore, errorStore)
              return
            }
            
            this.isLoading = false
            const img = new Image()
            img.addEventListener('load', () => {
              // Convert points from 2D array format [[x,y],[x,y],...] to flat array [x,y,x,y,...]
              if (resp.data.points && Array.isArray(resp.data.points) && Array.isArray(resp.data.points[0])) {
                // New API format: [[x,y],[x,y],[x,y],[x,y]]
                // this.points = resp.data.points.flat()
                this.points = []
              } else {
                // Old API format or already flat: [x,y,x,y,x,y,x,y]
                this.points = resp.data.points
              }
              this.image = img
            });

            // Converting base64 to blob
            let ImageURL = resp.data.image
            var block = ImageURL.split(';');
            var contentType = block[0].split(':')[1];
            var realData = block[1].split(',')[1];
            var blob = this.b64toBlob(realData, contentType);
            var objectURL = URL.createObjectURL(blob);
            img.src = objectURL

            // Initially set the original file, but this will be updated when user edits
            receiptStore.setReceiptImage(this.file)
          } else {
            this.isLoading = false
            errorStore.handleError(this.$t('The uploaded file is not a receipt. Please upload a valid receipt to continue.'))
            this.resetState()
          }
        })
        .catch(error => {
          console.error('Error in receipt scanning:', error)
          this.isLoading = false
          errorStore.handleError(this.$t('The uploaded file is not a receipt. Please upload a valid receipt to continue.'))
          this.resetState()
        })
    },
    fallbackToBinaryMode(formData, receiptStore, errorStore) {
      // Build a fresh FormData — the original instance is already consumed by the first request
      const binaryFormData = new FormData()
      binaryFormData.append('receipt', this.file)

      // Binary mode (no corner detection)
      // The endpoint returns raw binary image data, not JSON
      axios.post(`https://receipt.justsnap.eu/getConvertedImageWithPointsTF?binary=1`, binaryFormData, {
        responseType: 'blob'
      })
        .then(resp => {
          // Check if we got valid binary image data
          if (!resp.data || resp.data.size === 0 || !resp.data.image || resp.data.image.length === 0) {
            console.log('Binary mode returned empty, failing...')
            this.isLoading = false
            errorStore.handleError(this.$t('The uploaded file is not a receipt. Please upload a valid receipt to continue.'))
            this.resetState()
            return
          }
          
          this.isLoading = false
          const img = new Image()
          img.addEventListener('load', () => {
            // No points in binary mode
            this.points = []
            this.image = img
          });

          // Create object URL directly from the blob response
          var objectURL = URL.createObjectURL(resp.data);
          img.src = objectURL

          // Set the original file
          receiptStore.setReceiptImage(this.file)
        })
        .catch(error => {
          console.error('Error in binary mode receipt scanning:', error)
          this.isLoading = false
          errorStore.handleError(this.$t('The uploaded file is not a receipt. Please upload a valid receipt to continue.'))
          this.resetState()
        })
    },
    resetState() {
      this.image = null
      this.points = []
      this.editedImage = null
      this.$emit('input', null)
    },
    onImageEdit(editedBlob) {
      // Emit the edited image to parent component
      this.$emit('input', editedBlob)
      
      // Update the receipt store with the edited image
      if (editedBlob) {
        this.editedImage = editedBlob
        const receiptStore = useReceiptStore()
        receiptStore.setReceiptImage(editedBlob)
      }
    },
    b64toBlob (b64Data, contentType, sliceSize) {
      contentType = contentType || '';
      sliceSize = sliceSize || 512;

      var byteCharacters = atob(b64Data);
      var byteArrays = [];

      for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        var slice = byteCharacters.slice(offset, offset + sliceSize);

        var byteNumbers = new Array(slice.length);
        for (var i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        var byteArray = new Uint8Array(byteNumbers);

        byteArrays.push(byteArray);
      }

      var blob = new Blob(byteArrays, { type: contentType });
      return blob;
    }
  }
}
</script>
